/**
 * The crew iPad — plan §3.7 and §6.6.
 *
 * Its own frame: no left nav, no scope selector, nothing the office app has.
 * A shared tablet in a truck is used with gloves on, in the sun, by whoever
 * reaches it first — so the targets are big, the text is large, and the screen
 * says one thing at a time.
 *
 * The device holds the session; the person is identified by their PIN on every
 * punch. Nobody is "signed in" to this iPad — four people use it before 7 am.
 */
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { MeResponse } from '@rowted/shared'
import { PinPad, Toast } from '../components/overlays.js'
import { Avatar, Button, Card, EmptyState, SectionLabel, Tag } from '../components/primitives.js'
import { formatNumber } from '../lib/format.js'
import { request } from '../lib/api.js'
import { cn } from '../lib/cn.js'
import { IconSecurity } from '../frame/icons.js'

interface Member {
  id: string
  name: string
  isLead: boolean
  hasPin: boolean
  clockedInAt: string | null
  onVisitId: string | null
}

interface Stop {
  id: string
  sequence: number
  hoursForDay: string
  status: string
  isFinalVisit: boolean
  description: string
  jobReference: string
  jobTitle: string
  propertyId: string
  propertyName: string
  addressLine1: string
  city: string
  accessNotes: string | null
  contactNote: string | null
}

interface DayPayload {
  date: string
  crew: { id: string; name: string; truckLabel: string | null }
  members: Member[]
  stops: Stop[]
  activities: { id: string; name: string; isOverhead: boolean }[]
  materials: {
    id: string
    name: string
    unit: string
    qtyPlanned: string
    stopSequence: number
    propertyName: string
  }[]
  equipment: Piece[]
  plan: { stops: number; budgetedHours: number; firstStop: string | null }
}

/** A piece on the truck today, and whether it has had its morning check. */
interface Piece {
  id: string
  name: string
  assetTag: string
  meterKind: 'none' | 'miles' | 'hours'
  visitId: string | null
  checkedToday: boolean
}

const clockTime = (value: string | null): string => {
  if (!value) return ''
  return new Date(value).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit',
  }).toLowerCase()
}

/*
 * The iPad remembers which device it is. The office opens the app once with
 * `?crew=…&device=…`, and after that the truck's iPad is just a bookmark: the
 * crew never types either.
 *
 * A PIN is only accepted from a registered crew iPad (§G.3), and an iPad
 * cannot know its own registration without being told — so this is told once
 * and kept, rather than asked for at the keypad, where it would be four people
 * deep in a morning and nobody's job.
 */
const deviceKey = (crewId: string) => `rowted.crew-device.${crewId}`

function rememberedDevice(crewId: string, fromUrl: string | null): string | null {
  try {
    if (fromUrl) {
      window.localStorage.setItem(deviceKey(crewId), fromUrl)
      return fromUrl
    }
    return window.localStorage.getItem(deviceKey(crewId))
  } catch {
    // Private browsing, or storage turned off. The punch still works when the
    // session itself carries the device, which is the normal iPad case.
    return fromUrl
  }
}

export function CrewApp({ me }: { me: MeResponse }) {
  const [params] = useSearchParams()
  const crewId = params.get('crew')
  const deviceId = crewId ? rememberedDevice(crewId, params.get('device')) : null

  const [day, setDay] = useState<DayPayload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [person, setPerson] = useState<Member | null>(null)
  const [pin, setPin] = useState('')
  const [targetStop, setTargetStop] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 20000)
    return () => clearInterval(timer)
  }, [])

  const load = useCallback(async () => {
    if (!crewId) return
    try {
      setDay(await request<DayPayload>(`/crew-app/day?crewId=${crewId}`))
      setError(null)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Today could not load.')
    }
  }, [crewId])

  useEffect(() => { void load() }, [load])

  if (!crewId) {
    /*
     * Reached without a crew, which on a truck's iPad means it was never set
     * up — but on a crew member's own phone just means they opened the app
     * they were pointed at. Their hours are the thing they actually wanted,
     * so this offers them rather than ending the road here.
     */
    return (
      <CrewFrame tenant={me.tenant.name} title="Crew app">
        <EmptyState
          title="Which crew is this iPad for?"
          body="Open this screen with a crew, and it will stay on that crew. The office sets this up once per truck. On your own phone, your hours are what you are after."
          action={{ label: 'My hours', onClick: () => { window.location.href = '/my-hours' } }}
        />
      </CrewFrame>
    )
  }

  if (error) {
    return (
      <CrewFrame tenant={me.tenant.name} title="Crew app">
        <EmptyState title="Today could not load" body={error}
          action={{ label: 'Try again', onClick: () => void load() }} />
      </CrewFrame>
    )
  }
  if (!day) {
    return (
      <CrewFrame tenant={me.tenant.name} title="Crew app">
        <p className="p-6 text-body text-text-muted">Loading…</p>
      </CrewFrame>
    )
  }

  async function punch() {
    if (!person) return
    setBusy(true)
    try {
      const result = await request<{ action: string }>('/crew-app/punch', {
        method: 'POST',
        body: {
          userId: person.id,
          pin,
          visitId: targetStop,
          deviceId,
        },
      })
      setPin('')
      setPerson(null)
      setTargetStop(null)
      await load()
      setNotice(result.action === 'clocked_out'
        ? 'Clocked out. Have a good one.'
        : result.action === 'switched'
          ? 'On the next stop.'
          : 'Clocked in.')
    } catch (cause) {
      setPin('')
      setNotice(cause instanceof Error ? cause.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  const inCount = day.members.filter((member) => member.clockedInAt).length

  // Before anybody has punched, the iPad is a clock-in screen. Once the day is
  // under way it is a day view — which is what the crew is actually looking at.
  const showDay = inCount > 0 && !person

  return (
    <CrewFrame
      tenant={me.tenant.name}
      title={`${day.crew.name} · ${longDate(day.date)}`}
      right={day.crew.truckLabel
        ? `${day.crew.truckLabel} · ${inCount} of ${day.members.length} in`
        : `${inCount} of ${day.members.length} in`}
      chrome={showDay}
    >
      {showDay
        ? (
          <DayView
            day={day}
            onPunch={(member, visitId) => {
              setPerson(member)
              setTargetStop(visitId ?? null)
              setPin('')
            }}
          />
        )
        : (
          <div className="grid min-h-0 flex-1 lg:grid-cols-2">
            {/* --- the dark roster panel (§3.7) --- */}
            <div className="flex flex-col bg-crew-panel p-6 text-crew-panel-text">
              <div className="mb-8 flex items-baseline gap-3">
                <span className="text-card font-semibold">{me.tenant.name}</span>
                <span className="ml-auto text-body text-crew-panel-text/60">
                  {day.crew.truckLabel ?? day.crew.name}
                </span>
              </div>

              <p className="m-0 text-body text-crew-panel-text/60">{longDate(day.date)}</p>
              <p className="m-0 text-[56px] font-bold leading-none">
                {now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                  .toLowerCase()}
              </p>

              <p className="mb-2 mt-6 text-label text-crew-panel-text/50">
                {day.crew.name.toUpperCase()} · TODAY
              </p>

              <div className="flex flex-col gap-2">
                {day.members.map((member) => (
                  <button
                    key={member.id}
                    type="button"
                    onClick={() => { setPerson(member); setPin(''); setTargetStop(null) }}
                    className={cn(
                      'flex items-center gap-3 rounded-touch px-4 py-3 text-left',
                      person?.id === member.id
                        ? 'bg-crew-panel-text/20'
                        : 'bg-crew-panel-text/[0.06] hover:bg-crew-panel-text/10',
                    )}
                  >
                    <Avatar name={member.name} size={34} />
                    <span className="min-w-0 flex-1">
                      <span className="block text-body font-semibold">{member.name}</span>
                      <span className="block text-cell text-crew-panel-text/50">
                        {member.isLead ? 'Crew lead' : 'Crew member'}
                        {!member.hasPin && ' · no PIN yet'}
                      </span>
                    </span>
                    <span className={cn('text-cell font-semibold',
                      member.clockedInAt ? 'text-accent' : 'text-crew-panel-text/40')}>
                      {member.clockedInAt ? `In · ${clockTime(member.clockedInAt)}` : 'Not in yet'}
                    </span>
                  </button>
                ))}
              </div>

              <p className="mb-0 mt-auto pt-6 text-cell text-crew-panel-text/50">
                {day.plan.stops} {day.plan.stops === 1 ? 'stop' : 'stops'} ·{' '}
                {formatNumber(day.plan.budgetedHours)} h budgeted
                {day.plan.firstStop && ` · first stop ${day.plan.firstStop}`}
              </p>
            </div>

            {/* --- the keypad --- */}
            <div className="flex flex-col items-center justify-center p-6">
              {/*
                * Board 5 frame 2: the company, and a *verified* tag.
                *
                * `checkPin` has always been tenant-scoped, so a PIN typed here
                * could never have reached another company — what the subdomain
                * adds is that the crew can **see** it. This iPad was registered
                * at one address and shows that address's company; the line at
                * the foot says the consequence in words, because a shared iPad
                * on a truck is the device most likely to be handed to somebody
                * who has no idea which company it belongs to.
                */}
              <span className={cn('mb-4 inline-flex items-center gap-2 rounded-full',
                'bg-accent-soft px-3 py-1 text-cell font-semibold text-accent-dark')}
              >
                <IconSecurity size={13} />
                {me.tenant.name} · verified
              </span>
              <h2 className="m-0 text-[28px] font-bold text-text">
                {person ? `Hello, ${person.name.split(' ')[0]}` : 'Enter your PIN'}
              </h2>
              <p className="mb-6 mt-1 text-body text-text-muted">
                {person
                  ? (person.clockedInAt ? 'to clock out' : 'to clock in')
                  : 'pick your name, then your PIN'}
              </p>

              <PinPad
                value={pin}
                onChange={setPin}
                onSubmit={() => { if (person && !busy) void punch() }}
              />

              <p className={cn('mb-0 mt-6 max-w-[320px] text-center text-cell',
                'leading-[1.45] text-text-muted')}
              >
                This iPad only opens {me.tenant.name}. A PIN typed here can never
                reach another company.
              </p>

              {person && (
                <Button
                  variant="ghost"
                  className="mt-4"
                  onClick={() => { setPerson(null); setPin('') }}
                >
                  Not {person.name.split(' ')[0]}?
                </Button>
              )}

              <p className="mt-6 max-w-[38ch] text-center text-cell text-text-muted">
                Forgot your PIN? Ask your lead — they can reset it.
              </p>
            </div>
          </div>
        )}

      {notice && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2">
          <Toast onDismiss={() => setNotice(null)}>{notice}</Toast>
        </div>
      )}
    </CrewFrame>
  )
}

/** The crew app's own frame — §3.7: no nav, no scope, a 60px bar. */
/**
 * `chrome={false}` drops the bar entirely.
 *
 * The clock-in screen is a lock screen: it runs edge to edge, and the company
 * name and truck belong inside the dark panel where the crew is already
 * looking. Putting a bar above it would say the same words twice and make the
 * one full-bleed screen in the product look like a page.
 */
function CrewFrame({
  tenant, title, right, chrome = true, children,
}: {
  tenant: string
  title: string
  right?: string
  chrome?: boolean
  children: React.ReactNode
}) {
  return (
    <div className="flex h-app flex-col bg-bg">
      {chrome && (
        <header className="flex h-[60px] shrink-0 items-center gap-3 border-b border-line bg-surface px-5">
          <span className="text-card font-semibold text-text">{tenant}</span>
          <Tag>Crew app</Tag>
          <span className="text-body text-text">{title}</span>
          {right && <span className="ml-auto text-body text-text-muted">{right}</span>}
        </header>
      )}
      <main className="flex min-h-0 flex-1 flex-col overflow-y-auto">{children}</main>
    </div>
  )
}

/** Today's stops, the crew, and what is on the truck. */
function DayView({
  day, onPunch,
}: {
  day: DayPayload
  onPunch: (member: Member, visitId?: string) => void
}) {
  const done = day.stops.filter((stop) => stop.status === 'completed').length
  const checked = day.equipment.filter((piece) => piece.checkedToday).length
  const current = day.stops.find((stop) => stop.status === 'in_progress')
    ?? day.stops.find((stop) => stop.status !== 'completed')

  return (
    <div className="grid gap-4 p-5 lg:grid-cols-[1fr_minmax(280px,360px)]">
      <div>
        <div className="mb-3 flex flex-wrap items-baseline gap-2">
          <h2 className="m-0 text-[22px] font-bold text-text">Today’s stops</h2>
          <span className="text-body text-text-muted">
            {day.stops.length} {day.stops.length === 1 ? 'stop' : 'stops'} ·{' '}
            {formatNumber(day.plan.budgetedHours)} h budgeted · {done} of {day.stops.length} done
          </span>
        </div>

        {day.stops.length === 0 && (
          <EmptyState title="Nothing on today" body="No stops are scheduled for this crew." />
        )}

        <div className="flex flex-col gap-2.5">
          {day.stops.map((stop) => {
            const isNow = stop.id === current?.id
            const isDone = stop.status === 'completed'

            return (
              <div key={stop.id}>
                {/*
                  * Before the stop, not after it. "Read before Hershey
                  * Medical" underneath the Hershey card is an instruction
                  * arriving after the thing it is about — and the crew is
                  * already in the truck by then.
                  */}
                {isNow && (stop.accessNotes || stop.contactNote) && (
                  <div className="mb-2 rounded-card border border-dashed border-warn bg-warn-bg p-3">
                    <p className="m-0 text-body text-text">
                      <span className="font-bold">Read before {stop.propertyName}: </span>
                      {stop.accessNotes ?? stop.contactNote}
                    </p>
                  </div>
                )}

                <Card
                  className="flex flex-wrap items-start gap-4 p-4"
                  tone={isNow ? 'warn' : 'plain'}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-body font-bold',
                      isDone ? 'bg-accent text-white'
                        : isNow ? 'bg-warn text-white' : 'bg-surface-alt text-text-muted',
                    )}
                  >
                    {stop.sequence}
                  </span>

                  <div className="min-w-[220px] flex-1">
                    <p className="m-0 flex flex-wrap items-center gap-2">
                      <span className="text-[19px] font-bold text-text">{stop.propertyName}</span>
                      <Tag>{formatNumber(Number(stop.hoursForDay))} h</Tag>
                      {stop.isFinalVisit && <Tag>Last visit</Tag>}
                    </p>
                    <p className="m-0 text-body text-text-muted">
                      {stop.addressLine1}, {stop.city}
                    </p>
                    <p className="m-0 mt-0.5 text-body text-text">{stop.description}</p>
                  </div>

                  <div className="flex shrink-0 flex-col gap-2">
                    <Button
                      size="big"
                      onClick={() => window.open(
                        `https://maps.google.com/?q=${encodeURIComponent(
                          `${stop.addressLine1}, ${stop.city}`)}`, '_blank', 'noopener')}
                    >
                      Go
                    </Button>
                    {!isDone && (
                      <Button
                        size="big"
                        variant={isNow ? 'primary' : 'default'}
                        onClick={() => {
                          const member = day.members.find((candidate) => candidate.clockedInAt)
                          if (member) onPunch(member, stop.id)
                        }}
                      >
                        {isNow ? 'Open visit' : 'Start here'}
                      </Button>
                    )}
                    {/* §6.7: the close-out is a gate before clocking out on
                        the last day, so it has to be reachable from the stop
                        rather than only from a link somebody sends. */}
                    <Button
                      size="big"
                      onClick={() => { window.location.href = `/crew/closeout/${stop.id}` }}
                    >
                      Close out materials
                    </Button>
                  </div>
                </Card>
              </div>
            )
          })}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <Card className="p-4">
          <div className="mb-2 flex items-baseline justify-between">
            <SectionLabel>Crew</SectionLabel>
            <span className="text-cell text-text-muted">
              {day.members.filter((member) => member.clockedInAt).length} of{' '}
              {day.members.length} in
            </span>
          </div>
          <ul className="m-0 list-none space-y-2 p-0">
            {day.members.map((member) => (
              <li key={member.id} className="flex items-center gap-2.5">
                <Avatar name={member.name} size={30} />
                <span className="min-w-0 flex-1 truncate text-body text-text">
                  {member.name}
                  {member.isLead && <span className="text-text-muted"> · lead</span>}
                </span>
                <button
                  type="button"
                  onClick={() => onPunch(member)}
                  className={cn('text-body font-semibold',
                    member.clockedInAt ? 'text-accent' : 'text-danger')}
                >
                  {member.clockedInAt ? clockTime(member.clockedInAt) : 'not in'}
                </button>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="p-4">
          <SectionLabel className="mb-2 block">Materials · all stops</SectionLabel>
          {day.materials.length === 0
            ? (
              <p className="m-0 text-body text-text-muted">
                Nothing to load for today’s stops.
              </p>
            )
            : (
              <ul className="m-0 list-none space-y-1.5 p-0">
                {day.materials.map((material) => (
                  <li key={material.id} className="flex items-baseline gap-2">
                    <span className="min-w-0 flex-1 text-body text-text">
                      {material.name} · {formatNumber(Number(material.qtyPlanned))} {material.unit}
                    </span>
                    <span className="shrink-0 text-cell text-text-muted">
                      stop {material.stopSequence}
                    </span>
                  </li>
                ))}
              </ul>
            )}
        </Card>

        <Card className="p-4">
          <div className="mb-2 flex items-baseline justify-between">
            <SectionLabel>Truck &amp; equipment</SectionLabel>
            {day.equipment.length > 0 && (
              <span className={cn('text-cell',
                checked === day.equipment.length ? 'text-accent' : 'text-danger')}>
                {checked} of {day.equipment.length} checked
              </span>
            )}
          </div>

          {day.equipment.length === 0
            ? (
              <p className="m-0 text-body text-text-muted">
                {day.crew.truckLabel ?? 'Nothing assigned to this crew today.'}
              </p>
            )
            : (
              <ul className="m-0 list-none space-y-2 p-0">
                {day.equipment.map((piece) => (
                  <li key={piece.id} className="flex items-center gap-2">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-body text-text">{piece.name}</span>
                      <span className="block text-cell text-text-muted">{piece.assetTag}</span>
                    </span>
                    {piece.checkedToday
                      ? <Tag tone="ok">Checked</Tag>
                      : (
                        <Button
                          size="big"
                          variant="primary"
                          onClick={() => {
                            const visit = piece.visitId ? `&visit=${piece.visitId}` : ''
                            window.location.href =
                              `/crew/check/${piece.id}?crew=${day.crew.id}${visit}`
                          }}
                        >
                          Check
                        </Button>
                      )}
                  </li>
                ))}
              </ul>
            )}
        </Card>
      </div>
    </div>
  )
}

const longDate = (iso: string): string =>
  new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'long', month: 'short', day: 'numeric', timeZone: 'UTC',
  })
